import type {
  Address,
  AddressDraft,
  AddressDraftField,
  AddressPlace,
} from '../types/address.types';

/** What formatAddress reads: a saved address or an order's address snapshot. */
interface AddressParts {
  address_line: string;
  rt: string | null;
  rw: string | null;
  subdistrict: string | null;
  district: string | null;
  city: string | null;
  province: string | null;
}

/** Street, RT/RW and the region chain on one line. */
export const formatAddress = (address: AddressParts) => {
  const rtRw = [address.rt && `RT ${address.rt}`, address.rw && `RW ${address.rw}`]
    .filter(Boolean)
    .join('/');
  return [
    address.address_line,
    rtRw,
    address.subdistrict,
    address.district,
    address.city,
    address.province,
  ]
    .filter(Boolean)
    .join(', ');
};

export const EMPTY_ADDRESS_DRAFT: AddressDraft = { label: '', rt: '', rw: '', address_line: '' };

export const addressDraftOf = (address: Address): AddressDraft => ({
  label: address.label ?? '',
  province: { id: address.province_id, name: address.province },
  city: { id: address.city_id, name: address.city },
  district: { id: address.district_id, name: address.district },
  subdistrict: {
    id: address.subdistrict_id,
    name: address.subdistrict,
    postal_code: address.postal_code,
  },
  rt: address.rt ?? '',
  rw: address.rw ?? '',
  address_line: address.address_line,
});

/** One entry per field (undefined when fine), so a server error can be matched to a field. */
export const addressDraftErrors = (
  draft: AddressDraft
): Record<AddressDraftField, string | undefined> => ({
  label: undefined,
  province: draft.province ? undefined : 'Provinsi wajib diisi.',
  city: draft.city ? undefined : 'Kota/Kabupaten wajib diisi.',
  district: draft.district ? undefined : 'Kecamatan wajib diisi.',
  subdistrict: draft.subdistrict ? undefined : 'Kelurahan/Desa wajib diisi.',
  rt: undefined,
  rw: undefined,
  address_line: draft.address_line.trim() ? undefined : 'Alamat lengkap wajib diisi.',
});

const blankToNull = (value: string) => value.trim() || null;

/** The request fields of a draft; null while a region is missing. */
export const addressPlaceOf = (draft: AddressDraft): AddressPlace | null => {
  const { province, city, district, subdistrict } = draft;
  if (!province || !city || !district || !subdistrict) return null;
  return {
    label: blankToNull(draft.label),
    address_line: draft.address_line.trim(),
    rt: blankToNull(draft.rt),
    rw: blankToNull(draft.rw),
    province_id: province.id,
    city_id: city.id,
    district_id: district.id,
    subdistrict_id: subdistrict.id,
  };
};

const DRAFT_FIELDS: AddressDraftField[] = [
  'label',
  'province',
  'city',
  'district',
  'subdistrict',
  'rt',
  'rw',
  'address_line',
];

/** The form field a server error `type` names (region ids are sent with an `_id` suffix). */
export const addressDraftFieldOf = (type: string): AddressDraftField | undefined => {
  const field = type.replace(/_id$/, '');
  return DRAFT_FIELDS.find((draftField) => draftField === field);
};
